import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Plus, Eye, Power, Save, Trash2, Copy, RotateCcw, Upload, Monitor, Smartphone, Tablet,
  MousePointer2, Snowflake, Lightbulb, TreePine, Ghost, Leaf, Sparkles, Star, Image as ImageIcon, X,
  CloudFog, CloudRain, PartyPopper, Heart, Egg, Sun, Flag,
} from "lucide-react";
import { toast } from "sonner";
import { useSiteData, FONT_PAIRS } from "../../context/SiteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardCls = "rounded-3xl bg-white border border-slate-100 p-6 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]";
const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand)]/30";

const OCCASION_META = {
  christmas: { emoji: "🎄", label: "Christmas" },
  halloween: { emoji: "🎃", label: "Halloween" },
  thanksgiving: { emoji: "🦃", label: "Thanksgiving" },
  newyear: { emoji: "🎆", label: "New Year" },
  easter: { emoji: "🐣", label: "Easter" },
  valentines: { emoji: "❤️", label: "Valentine's Day" },
  summer: { emoji: "☀️", label: "Summer" },
  autumn: { emoji: "🍂", label: "Autumn" },
  winter: { emoji: "❄️", label: "Winter" },
  nationalday: { emoji: "🇨🇭", label: "Swiss National Day" },
  custom: { emoji: "➕", label: "Custom" },
};

const EFFECT_META = [
  ["snow", "Falling snow", Snowflake],
  ["lights", "Light garland", Lightbulb],
  ["trees", "Christmas trees", TreePine],
  ["pumpkins", "Halloween ghosts", Ghost],
  ["leaves", "Autumn leaves", Leaf],
  ["fireworks", "Fireworks", Sparkles],
  ["rain", "Rain", CloudRain],
  ["fog", "Fog / smoke", CloudFog],
  ["confetti", "Confetti", PartyPopper],
];

const ADD_ITEMS = [
  { kind: "trees", label: "Pine tree", Icon: TreePine, color: "#047857" },
  { kind: "pumpkins", label: "Ghost", Icon: Ghost, color: "#ea580c" },
  { kind: "star", label: "Star", Icon: Star, color: "#d97706" },
  { kind: "snowflake", label: "Snowflake", Icon: Snowflake, color: "#2563eb" },
  { kind: "lights", label: "Light", Icon: Lightbulb, color: "#d97706" },
  { kind: "leaves", label: "Leaf", Icon: Leaf, color: "#b45309" },
  { kind: "heart", label: "Heart", Icon: Heart, color: "#e11d48" },
  { kind: "sun", label: "Sun", Icon: Sun, color: "#f59e0b" },
  { kind: "flag", label: "Flag", Icon: Flag, color: "#dc2626" },
  { kind: "egg", label: "Egg", Icon: Egg, color: "#059669" },
];

const DEFAULT_PARTICLE = { count: 30, speed: 1.0, direction: "down", opacity: 0.85, size: 14 };
const PAGE_KEYS = ["all", "home", "landing"];
const EMPTY_PAGE = { type: "", color: "", gradient: "", image: "", video: "", brightness: 1.0, blur: 0, overlay: 0.0, position: "center", text_color: "", button_color: "" };

const mergeDesign = (d) => ({
  accent_color: "", font_pair: "", background_video: "", logo: "",
  ...(d || {}),
  effects: Object.fromEntries(EFFECT_META.map(([k]) => [k, { home: false, landing: false, ...(((d || {}).effects || {})[k] || {}) }])),
  particle_cfg: {
    snow: { ...DEFAULT_PARTICLE, count: 36, ...((d || {}).particle_cfg || {}).snow },
    leaves: { ...DEFAULT_PARTICLE, count: 20, ...((d || {}).particle_cfg || {}).leaves },
    fireworks: { ...DEFAULT_PARTICLE, count: 14, direction: "up", opacity: 0.9, size: 16, ...((d || {}).particle_cfg || {}).fireworks },
    rain: { ...DEFAULT_PARTICLE, count: 40, speed: 2.0, opacity: 0.5, size: 10, ...((d || {}).particle_cfg || {}).rain },
    fog: { ...DEFAULT_PARTICLE, count: 8, speed: 0.5, direction: "left", opacity: 0.35, size: 90, ...((d || {}).particle_cfg || {}).fog },
    confetti: { ...DEFAULT_PARTICLE, count: 30, opacity: 0.9, size: 10, ...((d || {}).particle_cfg || {}).confetti },
  },
  bulbs: { count: 24, speed: 1.0, opacity: 1.0, ...((d || {}).bulbs || {}) },
  items: (d || {}).items || [],
  custom_particles: (d || {}).custom_particles || [],
  page: Object.fromEntries(PAGE_KEYS.map((k) => [k, { ...EMPTY_PAGE, ...(((d || {}).page || {})[k] || {}) }])),
});

/* =============================== Design Studio =============================== */
export const ThemeStudioTab = () => {
  const { refreshTheme } = useSiteData();
  const [themes, setThemes] = useState(null);
  const [studio, setStudio] = useState(null); // { base, edit, device, page, selectedItem }

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/themes`, { withCredentials: true });
      setThemes(data);
    } catch {
      toast.error("Could not load themes.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openStudio = (t) => {
    const edit = t.has_draft && t.draft ? { ...t, ...t.draft, design: mergeDesign(t.draft.design) } : { ...t, design: mergeDesign(t.design) };
    setStudio({ base: t, edit, device: "desktop", page: "home", selectedItem: null, dirty: !!t.has_draft });
  };

  const toggle = async (t) => {
    try {
      const { data } = await axios.post(`${API}/themes/${t.id}/toggle`, {}, { withCredentials: true });
      setThemes((prev) =>
        prev.map((x) => (x.id === t.id ? { ...x, enabled: data.enabled } : data.enabled ? { ...x, enabled: false } : x))
      );
      toast.success(data.enabled ? `${t.name} is live on the website now.` : `${t.name} is off — standard design restored.`);
      refreshTheme();
    } catch {
      toast.error("Could not switch the theme.");
    }
  };

  const activateNow = async (t) => {
    try {
      await axios.put(`${API}/themes/${t.id}`, { enabled: true, activation_mode: "manual" }, { withCredentials: true });
      setThemes((prev) => prev.map((x) => (x.id === t.id ? { ...x, enabled: true, activation_mode: "manual" } : { ...x, enabled: false })));
      toast.success(`${t.name} is live now — manual activation ignores all dates.`);
      refreshTheme();
    } catch {
      toast.error("Could not activate the theme.");
    }
  };

  const setMode = async (t, mode) => {
    try {
      await axios.put(`${API}/themes/${t.id}`, { activation_mode: mode }, { withCredentials: true });
      setThemes((prev) => prev.map((x) => (x.id === t.id ? { ...x, activation_mode: mode } : x)));
      toast.success(mode === "manual" ? "Manual mode — stays on until you switch it off." : "Automatic mode — follows the schedule dates.");
      refreshTheme();
    } catch {
      toast.error("Could not change the activation mode.");
    }
  };

  const restoreDefault = async () => {
    if (!window.confirm("Turn off all themes and restore the standard website design?")) return;
    try {
      await axios.post(`${API}/themes/restore-default`, {}, { withCredentials: true });
      setThemes((prev) => prev.map((x) => ({ ...x, enabled: false })));
      toast.success("Standard website design restored.");
      refreshTheme();
    } catch {
      toast.error("Could not restore the default design.");
    }
  };

  const duplicate = async (t) => {
    try {
      await axios.post(`${API}/themes/${t.id}/duplicate`, {}, { withCredentials: true });
      toast.success(`"${t.name}" duplicated.`);
      load();
    } catch {
      toast.error("Could not duplicate the theme.");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}" permanently? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/themes/${t.id}`, { withCredentials: true });
      toast.success("Theme deleted.");
      if (studio?.edit?.id === t.id) setStudio(null);
      load();
      refreshTheme();
    } catch {
      toast.error("Could not delete the theme.");
    }
  };

  const create = async () => {
    try {
      const { data } = await axios.post(`${API}/themes`, { name: "New Custom Theme", occasion: "custom" }, { withCredentials: true });
      toast.success("Custom theme created — design it now.");
      await load();
      openStudio({ ...data, has_draft: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create the theme.");
    }
  };

  if (studio) {
    return (
      <StudioEditor
        studio={studio}
        setStudio={setStudio}
        onClose={(reload) => { setStudio(null); if (reload) load(); }}
        onChanged={() => { load(); refreshTheme(); }}
      />
    );
  }

  const activeTheme = (themes || []).find((t) => t.enabled);

  return (
    <div className="space-y-6" data-testid="theme-studio-tab">
      <div className={cardCls}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-display font-bold text-lg text-[#0b132b]">Theme Design Studio</h2>
            <p className="text-xs text-slate-400 mt-1 max-w-2xl">
              Activate any theme on any date — manual activation always wins and ignores the schedule. Turning one ON turns the others OFF, and it stays live until you change or disable it.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={restoreDefault}
              data-testid="studio-restore-default-button"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 text-slate-600 text-sm font-bold px-4 py-2.5 hover:bg-slate-50 transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> Restore Default Website
            </button>
            <button
              onClick={create}
              data-testid="studio-create-button"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-5 py-2.5 shadow-lg shadow-[var(--brand)]/30 hover:bg-[var(--brand-dark)] transition-colors"
            >
              <Plus className="w-4 h-4" /> Create Custom Theme
            </button>
          </div>
        </div>
        <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50/70 px-4 py-3 flex items-center gap-3" data-testid="studio-active-banner">
          <span className={`w-2.5 h-2.5 rounded-full ${activeTheme ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`} />
          <p className="text-sm text-slate-600">
            {activeTheme ? (
              <>Currently live: <span className="font-bold text-[#0b132b]">{activeTheme.name}</span>
                <span className="ml-2 text-[10px] font-bold uppercase tracking-wide bg-slate-200 text-slate-600 rounded-full px-2 py-0.5">
                  {(activeTheme.activation_mode || "manual") === "auto" ? "Automatic" : "Manual"}
                </span>
              </>
            ) : (
              <>No theme active — the website is showing its <span className="font-bold text-[#0b132b]">standard design</span>.</>
            )}
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="studio-theme-grid">
        {(themes || []).map((t) => {
          const meta = OCCASION_META[t.occasion] || OCCASION_META.custom;
          return (
            <div key={t.id} className={`${cardCls} !p-5 flex flex-col gap-4`} data-testid={`studio-card-${t.occasion}-${t.id.slice(0, 4)}`}>
              <div className="flex items-start gap-3">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shrink-0 border"
                  style={{ background: t.design?.accent_color ? `${t.design.accent_color}14` : "#f8fafc", borderColor: "#e2e8f0" }}
                  data-testid={`studio-emoji-${t.occasion}`}
                >
                  {meta.emoji}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-[#0b132b] truncate">{t.name}</p>
                  <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>{meta.label}</span>
                    {t.enabled && <span className="text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">Live now</span>}
                    {t.has_draft && <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">Unpublished draft</span>}
                  </p>
                </div>
                <button
                  onClick={() => toggle(t)}
                  data-testid={`studio-toggle-${t.occasion}`}
                  aria-label={t.enabled ? "Deactivate" : "Activate"}
                  className={`w-11 h-7 rounded-full transition-colors relative shrink-0 ${t.enabled ? "bg-[var(--brand)]" : "bg-slate-200"}`}
                >
                  <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all ${t.enabled ? "start-5" : "start-1"}`} />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => (t.enabled ? toggle(t) : activateNow(t))}
                  data-testid={`studio-activate-${t.occasion}`}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-full text-xs font-bold px-3 py-2.5 transition-colors ${
                    t.enabled
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                      : "bg-[var(--brand)] text-white shadow-lg shadow-[var(--brand)]/30 hover:bg-[var(--brand-dark)]"
                  }`}
                >
                  <Power className="w-3.5 h-3.5" /> {t.enabled ? "Deactivate" : "Activate Now"}
                </button>
                <div className="flex rounded-full border border-slate-200 p-0.5 shrink-0" data-testid={`studio-mode-${t.occasion}`}>
                  {[{ id: "manual", label: "Manual" }, { id: "auto", label: "Auto" }].map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setMode(t, m.id)}
                      data-testid={`studio-mode-${m.id}-${t.occasion}`}
                      className={`text-[10px] font-bold px-2.5 py-1.5 rounded-full transition-colors ${
                        (t.activation_mode || "manual") === m.id ? "bg-[#0b132b] text-white" : "text-slate-500 hover:text-[#0b132b]"
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
              {(t.activation_mode || "manual") === "auto" && (t.start || t.end) && (
                <p className="-mt-2 text-[10px] text-slate-400">Shows automatically {t.start || "?"} → {t.end || "?"} (MM-DD).</p>
              )}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openStudio(t)}
                  data-testid={`studio-open-${t.occasion}`}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#0b132b] text-white text-xs font-bold px-4 py-2.5 hover:bg-slate-800 transition-colors"
                >
                  <MousePointer2 className="w-3.5 h-3.5" /> Edit
                </button>
                <button onClick={() => window.open(`/?theme_preview=${t.id}`, "_blank", "noopener")} data-testid={`studio-preview-${t.occasion}`} className="p-2.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors" aria-label="Preview live">
                  <Eye className="w-4 h-4" />
                </button>
                <button onClick={() => duplicate(t)} data-testid={`studio-duplicate-${t.occasion}`} className="p-2.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors" aria-label="Duplicate">
                  <Copy className="w-4 h-4" />
                </button>
                <button onClick={() => remove(t)} data-testid={`studio-delete-${t.occasion}`} className="p-2.5 rounded-xl border border-red-100 text-red-400 hover:bg-red-50 transition-colors" aria-label="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
        {!themes && <p className="text-sm text-slate-400 col-span-full">Loading themes…</p>}
      </div>
    </div>
  );
};

/* =============================== Studio Editor =============================== */
const StudioEditor = ({ studio, setStudio, onClose, onChanged }) => {
  const { refreshTheme } = useSiteData();
  const { edit, device, page, selectedItem } = studio;
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const itemFileRef = useRef(null);
  const particleFileRef = useRef(null);
  const bgFileRef = useRef(null);
  const bgFileRef2 = useRef(null);
  const dragState = useRef(null);
  const saveTimer = useRef(null);
  const firstRun = useRef(true);

  const setEdit = (patch) => setStudio((s) => ({ ...s, edit: { ...s.edit, ...patch }, dirty: true }));
  const setDesign = (patch) => setEdit({ design: { ...edit.design, ...patch } });

  // Auto-save draft (debounced)
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setStudio((s) => (s ? { ...s, saving: true } : s));
      try {
        await axios.put(`${API}/themes/${edit.id}`, { ...payloadOf(edit), save_draft: true }, { withCredentials: true });
        setStudio((s) => (s ? { ...s, saving: false, lastSaved: new Date() } : s));
      } catch {
        setStudio((s) => (s ? { ...s, saving: false } : s));
      }
    }, 1200);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit]);

  const publish = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/themes/${edit.id}`, { ...payloadOf(edit), save_draft: true }, { withCredentials: true });
      await axios.post(`${API}/themes/${edit.id}/publish`, {}, { withCredentials: true });
      toast.success("Published — the design is live on the website now.");
      setStudio((s) => ({ ...s, dirty: false }));
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Publish failed — please retry.");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset this theme to its original design? Your changes will be lost.")) return;
    try {
      const { data } = await axios.post(`${API}/themes/${edit.id}/reset`, {}, { withCredentials: true });
      toast.success("Theme restored to its original design.");
      setStudio({ base: data, edit: { ...data, design: mergeDesign(data.design) }, device, page, selectedItem: null, dirty: false });
      onChanged();
    } catch {
      toast.error("Could not reset the theme.");
    }
  };

  const upload = async (file, apply) => {
    if (!file) return;
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: m } = await axios.post(`${API}/media`, data, { withCredentials: true });
      apply(m.url);
      toast.success("Uploaded.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    }
  };

  /* ---------- decoration items (drag & drop) ---------- */
  const items = edit.design.items;
  const setItems = (next) => setDesign({ items: next });
  const selItem = items.find((i) => i.id === selectedItem);

  const addItem = (kind, color, image = "") => {
    const item = {
      id: Math.random().toString(36).slice(2, 9), kind, image, glyph: "", color: color || "",
      size: image ? 64 : 48, opacity: 0.75, rotation: 0, animation: "", pages: ["home", "landing"],
      desktop: { x: 88, y: 82 }, tablet: { x: 86, y: 85 }, mobile: { x: 82, y: 88 },
    };
    setItems([...items, item]);
    setStudio((s) => ({ ...s, selectedItem: item.id }));
  };

  const duplicateItem = (id) => {
    const src = items.find((i) => i.id === id);
    if (!src) return;
    const copy = { ...src, id: Math.random().toString(36).slice(2, 9),
      desktop: { x: Math.min(94, src.desktop.x + 6), y: Math.min(94, src.desktop.y + 4) },
      tablet: { x: Math.min(94, (src.tablet || src.desktop).x + 6), y: (src.tablet || src.desktop).y },
      mobile: { x: Math.min(90, (src.mobile || src.desktop).x + 6), y: (src.mobile || src.desktop).y } };
    setItems([...items, copy]);
    setStudio((s) => ({ ...s, selectedItem: copy.id }));
  };

  /* ---------- custom particle systems (uploaded images) ---------- */
  const customParticles = edit.design.custom_particles;
  const setCustomParticles = (next) => setDesign({ custom_particles: next });
  const addCustomParticle = (image) => {
    setCustomParticles([...customParticles, {
      id: Math.random().toString(36).slice(2, 9), image,
      count: 12, speed: 1.0, direction: "down", animation: "", size: 28, opacity: 0.85,
      pages: ["home", "landing"],
    }]);
  };
  const updateCustomParticle = (id, patch) => setCustomParticles(customParticles.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const removeCustomParticle = (id) => setCustomParticles(customParticles.filter((c) => c.id !== id));

  /* ---------- page background editor ---------- */
  const [bgTarget, setBgTarget] = useState("all");
  const pageBg = edit.design.page[bgTarget] || EMPTY_PAGE;
  const setPageBg = (patch) => setDesign({ page: { ...edit.design.page, [bgTarget]: { ...pageBg, ...patch } } });

  const updateItem = (id, patch) => setItems(items.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  const removeItem = (id) => { setItems(items.filter((i) => i.id !== id)); setStudio((s) => ({ ...s, selectedItem: null })); };

  const onPointerDown = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    setStudio((s) => ({ ...s, selectedItem: item.id }));
    const rect = canvasRef.current.getBoundingClientRect();
    dragState.current = {
      id: item.id, rect, startX: e.clientX, startY: e.clientY,
      orig: { ...((device === "desktop" ? item.desktop : item[device] || item.desktop) || { x: 50, y: 50 }) },
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp, { once: true });
  };

  const onPointerMove = (e) => {
    const d = dragState.current;
    if (!d) return;
    const dx = ((e.clientX - d.startX) / d.rect.width) * 100;
    const dy = ((e.clientY - d.startY) / d.rect.height) * 100;
    const x = Math.min(98, Math.max(2, d.orig.x + dx));
    const y = Math.min(98, Math.max(2, d.orig.y + dy));
    updateItem(d.id, { [device]: { x, y } });
  };

  const onPointerUp = () => {
    dragState.current = null;
    window.removeEventListener("pointermove", onPointerMove);
  };

  const pcfg = (kind) => edit.design.particle_cfg[kind];
  const setPcfg = (kind, patch) =>
    setDesign({ particle_cfg: { ...edit.design.particle_cfg, [kind]: { ...edit.design.particle_cfg[kind], ...patch } } });

  const effectOn = (kind) => !!edit.design.effects[kind]?.[page];
  const setEffect = (kind, on) => setDesign({ effects: { ...edit.design.effects, [kind]: { ...edit.design.effects[kind], [page]: on } } });

  const accent = edit.design.accent_color || "#e11d48";

  return (
    <div className="space-y-5" data-testid="studio-editor">
      {/* top bar */}
      <div className={`${cardCls} !p-4 sticky top-0 z-20`}>
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={() => onClose(true)} data-testid="studio-back-button" className="rounded-full border border-slate-200 text-slate-500 text-xs font-bold px-4 py-2 hover:bg-slate-50 transition-colors">
            ← Themes
          </button>
          <p className="font-display font-bold text-[#0b132b] text-sm truncate max-w-[180px]">{edit.name}</p>
          <div className="flex items-center rounded-full border border-slate-200 p-0.5">
            {[["desktop", Monitor], ["tablet", Tablet], ["mobile", Smartphone]].map(([d, Icon]) => (
              <button key={d} onClick={() => setStudio((s) => ({ ...s, device: d }))} data-testid={`studio-device-${d}`} className={`p-2 rounded-full transition-colors ${device === d ? "bg-[#0b132b] text-white" : "text-slate-400"}`} aria-label={d}>
                <Icon className="w-4 h-4" />
              </button>
            ))}
          </div>
          <div className="flex items-center rounded-full border border-slate-200 p-0.5 text-xs font-bold">
            {[["home", "Homepage"], ["landing", "Landing pages"]].map(([p, label]) => (
              <button key={p} onClick={() => setStudio((s) => ({ ...s, page: p }))} data-testid={`studio-page-${p}`} className={`px-3 py-1.5 rounded-full transition-colors ${page === p ? "bg-[#0b132b] text-white" : "text-slate-400"}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="ms-auto flex items-center gap-2">
            <span className="text-[11px] text-slate-400 font-semibold" data-testid="studio-draft-status">
              {studio.saving ? "Saving draft…" : studio.dirty ? "Draft auto-saves" : studio.lastSaved ? "Draft saved" : "No changes"}
            </span>
            <button onClick={reset} data-testid="studio-reset-button" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 text-slate-500 text-xs font-bold px-4 py-2 hover:bg-slate-50 transition-colors">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
            <button onClick={publish} disabled={busy} data-testid="studio-publish-button" className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand)] text-white text-xs font-bold px-5 py-2.5 shadow-lg shadow-[var(--brand)]/30 hover:bg-[var(--brand-dark)] transition-colors disabled:opacity-60">
              <Save className="w-3.5 h-3.5" /> {busy ? "Publishing…" : "Publish Now"}
            </button>
          </div>
        </div>
      </div>

      <div className="grid xl:grid-cols-[340px_1fr] gap-5 items-start">
        {/* controls */}
        <div className="space-y-5">
          <div className={cardCls}>
            <h3 className="font-display font-bold text-sm text-[#0b132b] mb-4">Content</h3>
            <div className="space-y-3">
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Theme name</span>
                <input value={edit.name} onChange={(e) => setEdit({ name: e.target.value })} className={`${inputCls} mt-1`} data-testid="studio-field-name" />
              </label>
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Eyebrow (small label above headline)</span>
                <input value={edit.eyebrow || ""} onChange={(e) => setEdit({ eyebrow: e.target.value })} className={`${inputCls} mt-1`} data-testid="studio-field-eyebrow" />
              </label>
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Headline</span>
                <input value={edit.headline || ""} onChange={(e) => setEdit({ headline: e.target.value })} className={`${inputCls} mt-1`} data-testid="studio-field-headline" />
              </label>
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Description</span>
                <textarea value={edit.description || ""} onChange={(e) => setEdit({ description: e.target.value })} rows={2} className={`${inputCls} mt-1`} data-testid="studio-field-description" />
              </label>
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Announcement bar</span>
                <input value={edit.announcement || ""} onChange={(e) => setEdit({ announcement: e.target.value })} className={`${inputCls} mt-1`} data-testid="studio-field-announcement" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Button text</span>
                  <input value={edit.button_text || ""} onChange={(e) => setEdit({ button_text: e.target.value })} className={`${inputCls} mt-1`} data-testid="studio-field-button" />
                </label>
                <label className="block">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Promo code</span>
                  <input value={edit.promo_code || ""} onChange={(e) => setEdit({ promo_code: e.target.value })} className={`${inputCls} mt-1`} data-testid="studio-field-promo" />
                </label>
              </div>
              <div className="rounded-2xl border border-slate-100 p-3">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1">Optional schedule</p>
                <p className="text-[10px] text-slate-400 mb-2">Leave empty for manual control. With dates, the theme shows only inside this window every year when switched ON.</p>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-[10px] font-semibold text-slate-400">Start</span>
                    <input type="date" value={edit.start ? `2026-${edit.start}` : ""} onChange={(e) => setEdit({ start: e.target.value ? e.target.value.slice(5) : "" })} className={`${inputCls} mt-1`} data-testid="studio-field-start" />
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-semibold text-slate-400">End</span>
                    <input type="date" value={edit.end ? `2026-${edit.end}` : ""} onChange={(e) => setEdit({ end: e.target.value ? e.target.value.slice(5) : "" })} className={`${inputCls} mt-1`} data-testid="studio-field-end" />
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className={cardCls}>
            <h3 className="font-display font-bold text-sm text-[#0b132b] mb-4">Colors, Fonts & Media</h3>
            <div className="space-y-4">
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Accent color</span>
                <div className="mt-1.5 flex items-center gap-2">
                  <input type="color" value={accent} onChange={(e) => setDesign({ accent_color: e.target.value })} className="w-10 h-10 rounded-xl border border-slate-200 cursor-pointer" data-testid="studio-accent-picker" />
                  <input value={edit.design.accent_color} onChange={(e) => setDesign({ accent_color: e.target.value })} placeholder="site default" className={`${inputCls} flex-1`} data-testid="studio-accent-hex" />
                  {edit.design.accent_color && (
                    <button onClick={() => setDesign({ accent_color: "" })} className="p-2 rounded-xl border border-slate-200 text-slate-400 hover:bg-slate-50" aria-label="Clear" data-testid="studio-accent-clear"><X className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Font pairing</span>
                <select value={edit.design.font_pair} onChange={(e) => setDesign({ font_pair: e.target.value })} className={`${inputCls} mt-1.5`} data-testid="studio-font-select">
                  <option value="">Site default</option>
                  {Object.entries(FONT_PAIRS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Banner image</span>
                <div className="mt-1.5 flex items-center gap-2">
                  <input value={edit.image || ""} onChange={(e) => setEdit({ image: e.target.value })} placeholder="https://… or upload" className={`${inputCls} flex-1`} data-testid="studio-image-url" />
                  <button onClick={() => fileRef.current?.click()} data-testid="studio-upload-button" className="p-2.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50" aria-label="Upload image">
                    <Upload className="w-4 h-4" />
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], (url) => setEdit({ image: url }))} />
                </div>
                {edit.image && <img src={edit.image} alt="" className="mt-2 h-16 w-full object-cover rounded-xl border border-slate-100" data-testid="studio-image-preview" />}
              </div>
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Mobile banner image (optional)</span>
                <input value={edit.image_mobile || ""} onChange={(e) => setEdit({ image_mobile: e.target.value })} placeholder="Mobile-specific image URL" className={`${inputCls} mt-1.5`} data-testid="studio-image-mobile-url" />
              </div>
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Banner logo / background video (optional)</span>
                <input value={edit.design.background_video} onChange={(e) => setDesign({ background_video: e.target.value })} placeholder="/api/media/file/… .mp4" className={`${inputCls} mt-1.5`} data-testid="studio-video-url" />
              </div>
            </div>
          </div>

          <div className={cardCls}>
            <h3 className="font-display font-bold text-sm text-[#0b132b] mb-1">Effects — {page === "home" ? "Homepage" : "Landing pages"}</h3>
            <p className="text-[11px] text-slate-400 mb-4">Switch effects on/off per page. Sliders control the animation.</p>
            <div className="space-y-3">
              {EFFECT_META.map(([kind, label, Icon]) => (
                <div key={kind} className="rounded-2xl border border-slate-100 p-3" data-testid={`studio-effect-${kind}`}>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700"><Icon className="w-4 h-4 text-slate-400" /> {label}</span>
                    <button
                      onClick={() => setEffect(kind, !effectOn(kind))}
                      data-testid={`studio-effect-toggle-${kind}`}
                      aria-label={`Toggle ${label}`}
                      className={`w-10 h-6 rounded-full transition-colors relative ${effectOn(kind) ? "bg-[var(--brand)]" : "bg-slate-200"}`}
                    >
                      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${effectOn(kind) ? "start-5" : "start-0.5"}`} />
                    </button>
                  </div>
                  {effectOn(kind) && ["snow", "leaves", "fireworks"].includes(kind) && (
                    <div className="mt-3 space-y-2.5">
                      <SliderRow label="Amount" min={0} max={120} value={pcfg(kind).count} onChange={(v) => setPcfg(kind, { count: v })} testid={`studio-${kind}-count`} />
                      <SliderRow label="Speed" min={0.25} max={3} step={0.25} value={pcfg(kind).speed} onChange={(v) => setPcfg(kind, { speed: v })} testid={`studio-${kind}-speed`} />
                      <SliderRow label="Size" min={6} max={32} value={pcfg(kind).size} onChange={(v) => setPcfg(kind, { size: v })} testid={`studio-${kind}-size`} />
                      <SliderRow label="Opacity" min={0.1} max={1} step={0.05} value={pcfg(kind).opacity} onChange={(v) => setPcfg(kind, { opacity: v })} testid={`studio-${kind}-opacity`} />
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-xs font-semibold text-slate-500">Direction</span>
                        <select value={pcfg(kind).direction} onChange={(e) => setPcfg(kind, { direction: e.target.value })} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold" data-testid={`studio-${kind}-direction`}>
                          <option value="down">Falling</option>
                          <option value="up">Rising</option>
                          <option value="left">Drift left</option>
                          <option value="right">Drift right</option>
                        </select>
                      </div>
                    </div>
                  )}
                  {effectOn(kind) && ["trees", "pumpkins"].includes(kind) && (
                    <p className="mt-2 text-[10px] text-slate-400">Renders as corner decorations. Add a draggable one below to place it anywhere.</p>
                  )}
                  {effectOn(kind) && kind === "lights" && (
                    <div className="mt-3 space-y-2.5">
                      <SliderRow label="Bulbs" min={4} max={60} value={edit.design.bulbs.count} onChange={(v) => setDesign({ bulbs: { ...edit.design.bulbs, count: v } })} testid="studio-bulbs-count" />
                      <SliderRow label="Speed" min={0.25} max={3} step={0.25} value={edit.design.bulbs.speed} onChange={(v) => setDesign({ bulbs: { ...edit.design.bulbs, speed: v } })} testid="studio-bulbs-speed" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className={cardCls}>
            <h3 className="font-display font-bold text-sm text-[#0b132b] mb-1">Decorations on the canvas</h3>
            <p className="text-[11px] text-slate-400 mb-4">Add elements, then drag them anywhere on the preview. They never block buttons or menus.</p>
            <div className="flex flex-wrap gap-2" data-testid="studio-add-items">
              {ADD_ITEMS.map(({ kind, label, Icon, color }) => (
                <button key={kind} onClick={() => addItem(kind, color)} data-testid={`studio-add-${kind}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 text-xs font-bold text-slate-600 px-3 py-2 hover:border-[var(--brand)] hover:text-[var(--brand)] transition-colors">
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
              <button onClick={() => itemFileRef.current?.click()} data-testid="studio-add-image" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 text-xs font-bold text-slate-600 px-3 py-2 hover:border-[var(--brand)] hover:text-[var(--brand)] transition-colors">
                <ImageIcon className="w-3.5 h-3.5" /> My image
              </button>
              <input ref={itemFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], (url) => addItem("custom-image", "", url))} />
            </div>
            {selItem && (
              <div className="mt-4 rounded-2xl border border-[var(--brand)]/30 bg-[var(--brand-soft)] p-4 space-y-3" data-testid="studio-item-panel">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-[#0b132b]">Selected decoration</p>
                  <div className="flex items-center gap-1">
                    <button onClick={() => duplicateItem(selItem.id)} data-testid="studio-item-duplicate" className="p-1.5 rounded-lg text-slate-500 hover:bg-white" aria-label="Duplicate decoration"><Copy className="w-4 h-4" /></button>
                    <button onClick={() => removeItem(selItem.id)} data-testid="studio-item-delete" className="p-1.5 rounded-lg text-red-400 hover:text-red-500" aria-label="Remove decoration"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <SliderRow label="Size" min={16} max={160} value={selItem.size} onChange={(v) => updateItem(selItem.id, { size: v })} testid="studio-item-size" />
                <SliderRow label="Opacity" min={0.1} max={1} step={0.05} value={selItem.opacity} onChange={(v) => updateItem(selItem.id, { opacity: v })} testid="studio-item-opacity" />
                <SliderRow label="Rotate" min={-180} max={180} step={5} value={selItem.rotation || 0} onChange={(v) => updateItem(selItem.id, { rotation: v })} testid="studio-item-rotation" />
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs font-semibold text-slate-500">Animation</span>
                  <select value={selItem.animation || ""} onChange={(e) => updateItem(selItem.id, { animation: e.target.value })} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold" data-testid="studio-item-animation">
                    <option value="">None</option>
                    <option value="float">Float</option>
                    <option value="bounce">Bounce</option>
                    <option value="rotate">Rotate</option>
                    <option value="spin-slow">Slow spin</option>
                    <option value="drift">Drift</option>
                    <option value="twinkle">Twinkle</option>
                  </select>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs font-semibold text-slate-500">Show on</span>
                  <div className="flex gap-3 text-xs font-semibold text-slate-600">
                    {[["home", "Home"], ["landing", "Landing"]].map(([p, label]) => (
                      <label key={p} className="flex items-center gap-1.5">
                        <input
                          type="checkbox"
                          checked={(selItem.pages || []).includes(p)}
                          onChange={(e) => updateItem(selItem.id, { pages: e.target.checked ? [...(selItem.pages || []), p] : (selItem.pages || []).filter((x) => x !== p) })}
                          className="w-3.5 h-3.5 accent-[var(--brand)]"
                          data-testid={`studio-item-page-${p}`}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
                <p className="text-[10px] text-slate-400">Editing position for: <b>{device}</b> (switch device above to position separately on tablet/mobile)</p>
              </div>
            )}
          </div>

          {/* Custom image particles */}
          <div className={cardCls}>
            <h3 className="font-display font-bold text-sm text-[#0b132b] mb-1">Custom image effects</h3>
            <p className="text-[11px] text-slate-400 mb-4">Upload a transparent PNG, SVG, WebP or GIF and make it fall, float or drift across the screen.</p>
            <button onClick={() => particleFileRef.current?.click()} data-testid="studio-particle-upload" className="inline-flex items-center gap-2 rounded-full border border-slate-200 text-xs font-bold text-slate-600 px-4 py-2.5 hover:border-[var(--brand)] hover:text-[var(--brand)] transition-colors">
              <Upload className="w-4 h-4" /> Upload decoration image
            </button>
            <input ref={particleFileRef} type="file" accept="image/png,image/webp,image/svg+xml,image/gif" className="hidden" onChange={(e) => upload(e.target.files?.[0], addCustomParticle)} data-testid="studio-particle-file" />
            <div className="mt-4 space-y-3">
              {customParticles.map((cp) => (
                <div key={cp.id} className="rounded-2xl border border-slate-100 p-3 space-y-2.5" data-testid={`studio-cparticle-${cp.id}`}>
                  <div className="flex items-center gap-3">
                    <img src={cp.image} alt="" className="w-10 h-10 object-contain rounded-lg bg-slate-50 border border-slate-100" />
                    <span className="text-xs font-semibold text-slate-500 flex-1 truncate">{cp.image.split("/").pop()}</span>
                    <button onClick={() => removeCustomParticle(cp.id)} data-testid={`studio-cparticle-delete-${cp.id}`} className="p-1.5 rounded-lg text-red-400 hover:bg-red-50" aria-label="Remove effect"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <SliderRow label="Amount" min={1} max={60} value={cp.count} onChange={(v) => updateCustomParticle(cp.id, { count: v })} testid={`studio-cparticle-count-${cp.id}`} />
                  <SliderRow label="Speed" min={0.25} max={3} step={0.25} value={cp.speed} onChange={(v) => updateCustomParticle(cp.id, { speed: v })} testid={`studio-cparticle-speed-${cp.id}`} />
                  <SliderRow label="Size" min={8} max={96} value={cp.size} onChange={(v) => updateCustomParticle(cp.id, { size: v })} testid={`studio-cparticle-size-${cp.id}`} />
                  <SliderRow label="Opacity" min={0.1} max={1} step={0.05} value={cp.opacity} onChange={(v) => updateCustomParticle(cp.id, { opacity: v })} testid={`studio-cparticle-opacity-${cp.id}`} />
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-semibold text-slate-500">Direction</span>
                    <select value={cp.direction} onChange={(e) => updateCustomParticle(cp.id, { direction: e.target.value })} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold" data-testid={`studio-cparticle-direction-${cp.id}`}>
                      <option value="down">Falling</option>
                      <option value="up">Floating up</option>
                      <option value="left">Drift left</option>
                      <option value="right">Drift right</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-semibold text-slate-500">Extra motion</span>
                    <select value={cp.animation || ""} onChange={(e) => updateCustomParticle(cp.id, { animation: e.target.value })} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold" data-testid={`studio-cparticle-animation-${cp.id}`}>
                      <option value="">None</option>
                      <option value="rotate">Rotate</option>
                      <option value="bounce">Bounce</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Site background */}
          <div className={cardCls}>
            <h3 className="font-display font-bold text-sm text-[#0b132b] mb-1">Website background</h3>
            <p className="text-[11px] text-slate-400 mb-4">Transform the whole site's backdrop while this theme is live.</p>
            <div className="flex items-center rounded-full border border-slate-200 p-0.5 text-xs font-bold w-fit mb-4">
              {[["all", "Whole site"], ["home", "Homepage"], ["landing", "Landing"]].map(([k, label]) => (
                <button key={k} onClick={() => setBgTarget(k)} data-testid={`studio-bg-target-${k}`} className={`px-3 py-1.5 rounded-full transition-colors ${bgTarget === k ? "bg-[#0b132b] text-white" : "text-slate-400"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-semibold text-slate-500">Type</span>
                <select value={pageBg.type} onChange={(e) => setPageBg({ type: e.target.value })} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold" data-testid="studio-bg-type">
                  <option value="">No change</option>
                  <option value="color">Solid color</option>
                  <option value="gradient">Gradient</option>
                  <option value="image">Full-screen image</option>
                  <option value="video">Background video</option>
                </select>
              </div>
              {pageBg.type === "color" && (
                <div className="flex items-center gap-2">
                  <input type="color" value={pageBg.color || "#ffffff"} onChange={(e) => setPageBg({ color: e.target.value })} className="w-10 h-10 rounded-xl border border-slate-200 cursor-pointer" data-testid="studio-bg-color" />
                  <input value={pageBg.color} onChange={(e) => setPageBg({ color: e.target.value })} className={`${inputCls} flex-1`} placeholder="#0c0a12" data-testid="studio-bg-color-hex" />
                </div>
              )}
              {pageBg.type === "gradient" && (
                <div className="space-y-2">
                  <input value={pageBg.gradient} onChange={(e) => setPageBg({ gradient: e.target.value })} className={inputCls} placeholder="linear-gradient(180deg, #0c0a12, #2a1206)" data-testid="studio-bg-gradient" />
                  <div className="flex flex-wrap gap-2">
                    {[
                      ["Spooky night", "linear-gradient(180deg,#0c0a12 0%,#1b0f22 55%,#2a1206 100%)"],
                      ["Warm autumn", "linear-gradient(180deg,#fff7ed 0%,#ffedd5 55%,#fed7aa 100%)"],
                      ["Winter sky", "linear-gradient(180deg,#f0f9ff 0%,#e0f2fe 100%)"],
                      ["Romantic rose", "linear-gradient(180deg,#fff1f2 0%,#ffe4e6 100%)"],
                      ["Midnight gold", "linear-gradient(180deg,#0b132b 0%,#1e1b4b 60%,#78350f 100%)"],
                    ].map(([label, g]) => (
                      <button key={label} onClick={() => setPageBg({ gradient: g })} data-testid={`studio-bg-preset-${label.toLowerCase().replace(/ /g, "-")}`} className="text-[10px] font-bold rounded-full border border-slate-200 px-2.5 py-1 text-slate-500 hover:border-[var(--brand)] hover:text-[var(--brand)] transition-colors">
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {pageBg.type === "image" && (
                <div className="flex items-center gap-2">
                  <input value={pageBg.image} onChange={(e) => setPageBg({ image: e.target.value })} className={`${inputCls} flex-1`} placeholder="https://… or upload" data-testid="studio-bg-image" />
                  <button onClick={() => bgFileRef.current?.click()} data-testid="studio-bg-upload" className="p-2.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50" aria-label="Upload background"><Upload className="w-4 h-4" /></button>
                  <input ref={bgFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], (url) => setPageBg({ image: url }))} />
                </div>
              )}
              {pageBg.type === "video" && (
                <div className="flex items-center gap-2">
                  <input value={pageBg.video} onChange={(e) => setPageBg({ video: e.target.value })} className={`${inputCls} flex-1`} placeholder="/api/media/file/… .mp4" data-testid="studio-bg-video" />
                  <button onClick={() => bgFileRef2.current?.click()} data-testid="studio-bg-video-upload" className="p-2.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50" aria-label="Upload video"><Upload className="w-4 h-4" /></button>
                  <input ref={bgFileRef2} type="file" accept="video/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], (url) => setPageBg({ video: url }))} />
                </div>
              )}
              {pageBg.type && (
                <>
                  <SliderRow label="Bright" min={0.3} max={1.4} step={0.05} value={pageBg.brightness} onChange={(v) => setPageBg({ brightness: v })} testid="studio-bg-brightness" />
                  <SliderRow label="Blur" min={0} max={12} value={pageBg.blur} onChange={(v) => setPageBg({ blur: v })} testid="studio-bg-blur" />
                  <SliderRow label="Overlay" min={0} max={0.8} step={0.05} value={pageBg.overlay} onChange={(v) => setPageBg({ overlay: v })} testid="studio-bg-overlay" />
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-semibold text-slate-500">Text color</span>
                    <div className="flex items-center gap-2">
                      <input type="color" value={pageBg.text_color || "#0b132b"} onChange={(e) => setPageBg({ text_color: e.target.value })} className="w-9 h-9 rounded-lg border border-slate-200 cursor-pointer" data-testid="studio-bg-text-color" />
                      <button onClick={() => setPageBg({ text_color: "" })} className="text-[10px] font-bold text-slate-400 underline" data-testid="studio-bg-text-clear">default</button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-semibold text-slate-500">Button color</span>
                    <div className="flex items-center gap-2">
                      <input type="color" value={pageBg.button_color || accent} onChange={(e) => setPageBg({ button_color: e.target.value })} className="w-9 h-9 rounded-lg border border-slate-200 cursor-pointer" data-testid="studio-bg-button-color" />
                      <button onClick={() => setPageBg({ button_color: "" })} className="text-[10px] font-bold text-slate-400 underline" data-testid="studio-bg-button-clear">default</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* live preview canvas */}
        <div className={cardCls} data-testid="studio-canvas-wrap">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Live preview — {device} · {page === "home" ? "homepage" : "landing page"}</p>
            <span className="text-[10px] text-slate-300 font-semibold">Drag decorations on the preview</span>
          </div>
          <div
            ref={canvasRef}
            data-testid="studio-canvas"
            className={`relative mx-auto overflow-hidden rounded-2xl border border-slate-200 bg-white select-none ${device === "mobile" ? "w-[320px]" : device === "tablet" ? "w-[640px] max-w-full" : "w-full"}`}
            style={{ height: 520, touchAction: "none", "--seasonal-accent": accent, "--seasonal-soft": `${accent}14` }}
          >
            <StudioMockup edit={edit} accent={accent} page={page} />
            <StudioFx edit={edit} page={page} />
            {items.filter((i) => (i.pages || ["home"]).includes(page)).map((item) => {
              const pos = (device === "desktop" ? item.desktop : item[device] || item.desktop) || { x: 50, y: 50 };
              const Icon = CORNER[item.kind] || Star;
              return (
                <div
                  key={item.id}
                  onPointerDown={(e) => onPointerDown(e, item)}
                  data-testid={`studio-item-${item.id}`}
                  className={`absolute cursor-grab active:cursor-grabbing ${selectedItem === item.id ? "ring-2 ring-[var(--brand)] ring-offset-2 rounded-lg" : ""}`}
                  style={{
                    left: `${pos.x}%`, top: `${pos.y}%`, width: item.size, height: item.size, opacity: item.opacity,
                    transform: `translate(-50%,-50%)${item.rotation ? ` rotate(${item.rotation}deg)` : ""}`,
                  }}
                >
                  {item.image ? (
                    <img src={item.image} alt="" className="w-full h-full object-contain pointer-events-none" draggable={false} />
                  ) : item.glyph ? (
                    <span className="block text-center pointer-events-none" style={{ fontSize: item.size * 0.85, lineHeight: 1 }}>{item.glyph}</span>
                  ) : (
                    <Icon className="w-full h-full pointer-events-none" style={{ color: item.color || accent }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const CORNER = { trees: TreePine, pumpkins: Ghost, star: Star, snowflake: Snowflake, lights: Lightbulb, leaves: Leaf };

const SliderRow = ({ label, min, max, step = 1, value, onChange, testid }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs font-semibold text-slate-500 w-16 shrink-0">{label}</span>
    <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="flex-1 accent-[var(--brand)]" data-testid={testid} />
    <span className="text-xs font-bold text-slate-600 w-9 text-end">{value}</span>
  </div>
);

/* -------- simplified homepage mockup behind the decorations -------- */
const StudioMockup = ({ edit, accent, page = "home" }) => {
  const spec = { ...(edit.design.page?.all || {}), ...Object.fromEntries(Object.entries((edit.design.page || {})[page] || {}).filter(([, v]) => v !== "" && v !== 0)) };
  const textColor = spec.text_color || "#0b132b";
  const muted = spec.text_color ? "rgba(255,255,255,0.25)" : "#e2e8f0";
  const bgStyle =
    spec.type === "color" && spec.color ? { background: spec.color }
    : spec.type === "gradient" && spec.gradient ? { background: spec.gradient }
    : {};
  const filterBits = [];
  if (spec.brightness && spec.brightness !== 1) filterBits.push(`brightness(${spec.brightness})`);
  if (spec.blur) filterBits.push(`blur(${spec.blur}px)`);
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={bgStyle}>
      {(spec.type === "image" && spec.image) || (spec.type === "video" && spec.video) ? (
        spec.type === "video" ? (
          <video src={spec.video} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" style={{ filter: filterBits.join(" ") || undefined, objectPosition: spec.position }} />
        ) : (
          <img src={spec.image} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: filterBits.join(" ") || undefined, objectPosition: spec.position }} />
        )
      ) : null}
      {spec.overlay > 0 && <div className="absolute inset-0 bg-black" style={{ opacity: spec.overlay }} />}
      <div className="relative">
    <div className="h-10 bg-white border-b border-slate-100 flex items-center px-4 gap-3">
      <div className="w-6 h-6 rounded-full" style={{ background: accent }} />
      <div className="flex gap-2">
        {[40, 32, 44, 36, 30].map((w, i) => <div key={i} className="h-1.5 rounded-full bg-slate-200" style={{ width: w }} />)}
      </div>
      <div className="ms-auto h-5 w-16 rounded-full" style={{ background: accent }} />
    </div>
    {edit.announcement && (
      <div className="bg-[#0b132b] text-white/90 text-[9px] font-semibold text-center py-1.5 px-2 truncate">
        ✦ {edit.announcement}
      </div>
    )}
    <div className="h-6 flex items-center gap-4 px-3 overflow-hidden" style={{ background: accent }}>
      {Array.from({ length: 6 }, (_, i) => <span key={i} className="text-[8px] font-bold text-white/90 whitespace-nowrap tracking-widest">GUIDED SWISS TOURS ✦</span>)}
    </div>
    <div className="grid grid-cols-2 gap-3 p-4">
      <div className="rounded-2xl bg-slate-100 h-40 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1589223050279-18d1f14059cf?crop=entropy&cs=srgb&fm=jpg&q=60&w=400" alt="" className="w-full h-full object-cover opacity-90" loading="lazy" />
      </div>
      <div className="pt-2 space-y-2">
        <div className="h-2.5 rounded-full w-20" style={{ background: `${accent}55` }} />
        <div className="h-4 rounded-full w-11/12" style={{ background: spec.text_color ? textColor : "#0b132b", opacity: 0.9 }} />
        <div className="h-4 rounded-full w-8/12" style={{ background: accent }} />
        <div className="h-2 rounded-full w-full" style={{ background: muted }} />
        <div className="h-2 rounded-full w-10/12" style={{ background: muted }} />
        <div className="h-7 w-24 rounded-full mt-2" style={{ background: spec.button_color || accent }} />
      </div>
    </div>
    {(edit.headline || edit.image) && (
      <div className="grid grid-cols-2 gap-3 px-4 pb-3 items-center">
        <div className="space-y-1.5">
          <div className="h-2 rounded-full w-16" style={{ background: `${accent}55` }} />
          <p className="text-[11px] font-extrabold leading-tight line-clamp-2" style={{ color: textColor }}>{edit.headline || "Seasonal offer"}</p>
          <p className="text-[8px] leading-snug line-clamp-2" style={{ color: spec.text_color ? `${spec.text_color}99` : "#94a3b8" }}>{edit.description}</p>
          {edit.button_text && <div className="inline-block text-[8px] font-bold text-white rounded-full px-2.5 py-1" style={{ background: spec.button_color || accent }}>{edit.button_text}</div>}
        </div>
        {edit.image && <img src={edit.image} alt="" className="h-24 w-full object-cover rounded-xl" loading="lazy" />}
      </div>
    )}
    <div className="grid grid-cols-3 gap-2 px-4">
      {[0, 1, 2].map((i) => (
        <div key={i} className="rounded-xl border p-1.5" style={{ borderColor: spec.text_color ? "rgba(255,255,255,0.15)" : "#f1f5f9", background: spec.text_color ? "rgba(255,255,255,0.06)" : "transparent" }}>
          <div className="h-10 rounded-lg" style={{ background: spec.text_color ? "rgba(255,255,255,0.12)" : "#f1f5f9" }} />
          <div className="h-1.5 rounded-full mt-1.5 w-10/12" style={{ background: muted }} />
          <div className="h-1.5 rounded-full mt-1 w-6/12" style={{ background: `${accent}88` }} />
        </div>
      ))}
    </div>
      </div>
    </div>
  );
};

/* -------- effects rendered inside the preview canvas -------- */
const StudioFx = ({ edit, page }) => {
  const fx = edit.design.effects;
  const [seeded] = useState(() =>
    Array.from({ length: 120 }, (_, i) => ({ left: (i * 37 + 13) % 100, delay: (i * 0.7) % 8, vary: 0.6 + ((i * 7) % 40) / 60 }))
  );
  const meta = { snow: ["fx-snow", "❄"], leaves: ["fx-leaf", "🍂"], fireworks: ["fx-spark", "✦"], rain: ["fx-rain", ""], fog: ["fx-fog", ""], confetti: ["fx-confetti", ""] };
  const dirCls = { down: "", up: "fx-rise", left: "fx-drift-left", right: "fx-drift-right" };
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {Object.keys(meta).map((kind) => {
        if (!fx[kind]?.[page]) return null;
        const cfg = edit.design.particle_cfg[kind] || DEFAULT_PARTICLE;
        const [cls, glyph] = meta[kind];
        const isShape = ["rain", "fog", "confetti"].includes(kind);
        return seeded.slice(0, Math.min(120, cfg.count)).map((p, i) => (
          <span
            key={`${kind}-${i}`}
            className={`fx-particle ${kind === "fireworks" ? "fx-spark" : ""} ${isShape ? cls : ""} ${isShape && kind === "confetti" ? `fx-confetti-${i % 6}` : ""} ${dirCls[cfg.direction] || ""}`}
            style={{
              left: `${p.left}%`,
              top: kind === "fireworks" && !dirCls[cfg.direction] ? `${5 + ((i * 13) % 45)}%` : undefined,
              animationDelay: `${p.delay}s`,
              animationDuration: `${10 / Math.max(0.25, cfg.speed)}s`,
              fontSize: isShape ? undefined : `${Math.max(4, cfg.size * p.vary)}px`,
              width: isShape ? (kind === "fog" ? cfg.size * 3 * p.vary : Math.max(4, cfg.size * p.vary)) : undefined,
              height: isShape ? (kind === "fog" ? cfg.size * p.vary : kind === "rain" ? cfg.size * 1.6 : cfg.size) : undefined,
              opacity: cfg.opacity,
              "--sway": "14px",
            }}
          >
            {glyph}
          </span>
        ));
      })}
      {(edit.design.custom_particles || []).filter((cp) => (cp.pages || ["home"]).includes(page) && cp.image).map((cp) =>
        seeded.slice(0, Math.min(60, cp.count)).map((p, i) => (
          <span
            key={`cp-${cp.id}-${i}`}
            className={`fx-particle ${dirCls[cp.direction] || ""}`}
            style={{ left: `${p.left}%`, animationDelay: `${p.delay}s`, animationDuration: `${10 / Math.max(0.25, cp.speed)}s`, opacity: cp.opacity, "--sway": "12px" }}
          >
            <img src={cp.image} alt="" style={{ width: cp.size * p.vary, height: cp.size * p.vary, objectFit: "contain" }} className={cp.animation === "rotate" ? "fx-anim-rotate" : cp.animation === "bounce" ? "fx-anim-bounce" : ""} />
          </span>
        ))
      )}
      {fx.lights?.[page] && (
        <div className="fx-lights" style={{ opacity: edit.design.bulbs.opacity }}>
          {Array.from({ length: Math.min(60, edit.design.bulbs.count) }, (_, i) => (
            <span key={i} className={`fx-bulb fx-bulb-${i % 4}`} style={{ animationDelay: `${(i % 6) * 0.3}s`, animationDuration: `${1.8 / Math.max(0.25, edit.design.bulbs.speed)}s` }} />
          ))}
        </div>
      )}
      {fx.trees?.[page] && !edit.design.items.some((i) => i.kind === "trees") && (
        <>
          <TreePine className="fx-corner-icon bottom-2 start-2 text-emerald-700/70" />
          <TreePine className="fx-corner-icon bottom-2 end-2 text-emerald-700/70 scale-x-[-1]" />
        </>
      )}
      {fx.pumpkins?.[page] && !edit.design.items.some((i) => i.kind === "pumpkins") && (
        <>
          <Ghost className="fx-corner-icon bottom-2 start-2 text-orange-500/70" />
          <Ghost className="fx-corner-icon bottom-2 end-2 text-orange-500/70 scale-x-[-1]" />
        </>
      )}
    </div>
  );
};

const payloadOf = (edit) => ({
  name: edit.name, occasion: edit.occasion, eyebrow: edit.eyebrow, headline: edit.headline,
  description: edit.description, button_text: edit.button_text, promo_code: edit.promo_code,
  image: edit.image, image_mobile: edit.image_mobile, announcement: edit.announcement,
  pages: edit.pages, popup: edit.popup, decorations: edit.decorations, design: edit.design,
  start: edit.start || "", end: edit.end || "",
});
